import React, { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../contexts/AuthContext'
import { getUserProfile, updateUserProfile } from '../services/userDataService'

// Slide-based walkthrough for a tool page. Opens by itself the first time a
// user sees the page (once per account, kept in user_profiles.onboarding_progress
// under `tours`), and again whenever the page's ? is pressed.
//
// The mobile ? lives in PageRouter's header, outside the page, so it opens the
// tour with a `bc-open-tour` window event carrying the tool id.

// Keyed by user as well as tool: the flag is per ACCOUNT, and a browser that
// two accounts log into must not let one account's skip hide the other's tour.
const seenKey = (userId, toolId) => `bc-tour-seen-${userId}-${toolId}`

const readLocalSeen = (userId, toolId) => {
  try { return localStorage.getItem(seenKey(userId, toolId)) === '1' } catch { return false }
}
const writeLocalSeen = (userId, toolId) => {
  try { localStorage.setItem(seenKey(userId, toolId), '1') } catch {}
}

// Re-read before writing: the Dashboard's checklist saves the same JSON column,
// so merging into a stale copy would drop whichever flags the other side set.
async function markSeenRemote(userId, toolId) {
  const { data } = await getUserProfile(userId)
  const progress = data?.onboarding_progress || {}
  if (progress.tours?.[toolId]) return
  await updateUserProfile(userId, {
    onboarding_progress: { ...progress, tours: { ...(progress.tours || {}), [toolId]: true } },
  })
}

// For a page's own ? button (the hook listens for this too)
export const openTour = (toolId) => window.dispatchEvent(new CustomEvent('bc-open-tour', { detail: toolId }))

export function useToolTour(toolId, { isVisible }) {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const decided = useRef(false)

  // An interrupted check (page hidden again, or React re-running the effect)
  // leaves `decided` unset so the next visit checks again.
  useEffect(() => {
    if (!isVisible || !user?.id || decided.current) return
    if (readLocalSeen(user.id, toolId)) { decided.current = true; return }

    let cancelled = false
    let timer
    getUserProfile(user.id).then(({ data }) => {
      if (cancelled) return
      if (data?.onboarding_progress?.tours?.[toolId]) {
        decided.current = true
        writeLocalSeen(user.id, toolId)
        return
      }
      timer = setTimeout(() => {
        decided.current = true
        setOpen(true)
      }, 500)
    })
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [isVisible, user?.id, toolId])

  useEffect(() => {
    const onOpen = (e) => { if (e.detail === toolId) setOpen(true) }
    window.addEventListener('bc-open-tour', onOpen)
    return () => window.removeEventListener('bc-open-tour', onOpen)
  }, [toolId])

  const close = useCallback(() => {
    setOpen(false)
    if (user?.id) {
      writeLocalSeen(user.id, toolId)
      markSeenRemote(user.id, toolId)
    }
  }, [toolId, user?.id])

  return { open, openTour: () => setOpen(true), close }
}

// Numbered badge pinned to the top-right corner of a part of a slide's
// visual. The part needs position: relative (or absolute) to anchor it;
// `style` nudges it off text it would otherwise cover.
export function TourMarker({ n, style }) {
  return (
    <span aria-hidden style={{
      position: 'absolute', top: '-9px', right: '-9px', zIndex: 2,
      width: '20px', height: '20px', borderRadius: '50%', boxSizing: 'border-box',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '11px', fontWeight: 800, color: '#fff', fontVariantNumeric: 'tabular-nums',
      background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: '2px solid #0b1324',
      boxShadow: '0 2px 8px rgba(37,99,235,0.5)', ...style,
    }}>
      {n}
    </span>
  )
}

// Numbered key under a visual carrying TourMarkers
export function MarkerKey({ labels, isMobile }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: isMobile ? '6px 10px' : '6px 14px', marginTop: isMobile ? '12px' : '16px' }}>
      {labels.map((label, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: '#94a3b8' }}>
          <span style={{ width: '16px', height: '16px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 800, color: '#fff', backgroundColor: '#2563eb' }}>{i + 1}</span>
          {label}
        </span>
      ))}
    </div>
  )
}

// Long-run profit sketch for a tour's last slide: climbs overall, with the
// dips a high-variance strategy really has along the way. `headline` and
// `sub` are the long-run return, e.g. "~20% POT" / "$20 for every $100 staked"
// `steady` draws a near-straight climb instead, for a strategy with no losing
// runs (arbitrage); `points` draws any other shape (values may dip below 0).
const SWINGY = [0, 4, 2, 9, 6, 13, 10, 18, 15, 14, 22, 19, 27, 24, 31, 29, 36, 33, 41, 46]
const STEADY = [0, 2, 5, 7, 10, 12, 15, 18, 20, 23, 26, 28, 31, 34, 36, 39, 42, 44, 47, 50]

export function ProfitSketch({ isMobile, headline, sub, steady = false, points }) {
  const pts = points || (steady ? STEADY : SWINGY)
  const w = 440, h = isMobile ? 130 : 150, pad = 6
  const max = Math.max(...pts)
  const min = Math.min(0, ...pts)
  const xy = pts.map((v, i) => [pad + (i / (pts.length - 1)) * (w - pad * 2), h - pad - ((v - min) / (max - min)) * (h - pad * 2)])
  const line = xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const area = `${line} L${xy[xy.length - 1][0].toFixed(1)},${h} L${xy[0][0].toFixed(1)},${h} Z`
  const [ex, ey] = xy[xy.length - 1]
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: isMobile ? 'none' : '460px' }}>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{ display: 'block', overflow: 'visible' }}>
        <defs>
          <linearGradient id="ttProfitFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34d399" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#ttProfitFill)" />
        <path d={line} fill="none" stroke="#34d399" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={ex} cy={ey} r="9" fill="#34d399" opacity="0.18" />
        <circle cx={ex} cy={ey} r="4.5" fill="#34d399" />
      </svg>
      <div style={{
        position: 'absolute', top: isMobile ? '0' : '4px', left: isMobile ? '0' : '8px',
        padding: '10px 14px', borderRadius: '12px',
        background: 'linear-gradient(160deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.04) 100%)',
        border: '1px solid rgba(16,185,129,0.3)', boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
      }}>
        <div style={{ fontSize: isMobile ? '22px' : '26px', fontWeight: 800, color: '#34d399', letterSpacing: '-0.02em', lineHeight: 1.1 }}>{headline}</div>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', marginTop: '2px' }}>{sub}</div>
      </div>
    </div>
  )
}

// "Recommended filters" settings card for a tour's filter slide.
// rows: [{ label, value, info }], `info` an optional InfoTip beside the label.
// Two columns; put any row carrying an (i) in the right-hand column so its
// popover has room (InfoTip align="end").
export function RecommendedFilters({ rows, isMobile }) {
  const lines = Math.ceil(rows.length / 2)
  return (
    <div className="tt-rise" style={{
      width: '100%', maxWidth: isMobile ? 'none' : '480px', boxSizing: 'border-box', borderRadius: '16px',
      background: 'linear-gradient(180deg, #0f1a2e 0%, #0a1222 100%)', border: '1px solid #26354f',
      boxShadow: '0 16px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: isMobile ? '8px 14px' : '12px 18px', borderBottom: '1px solid #1b2538' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
        <span style={{ fontSize: isMobile ? '12.5px' : '13.5px', fontWeight: 800, color: '#f1f5f9' }}>Recommended filters</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: isMobile ? '0 14px 2px' : '4px 18px 8px', columnGap: isMobile ? '14px' : '28px' }}>
        {rows.map((f, i) => (
          <div key={f.label} style={{
            display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between',
            gap: isMobile ? '2px' : '10px', minWidth: 0, padding: isMobile ? '5px 0' : '10px 0',
            borderBottom: Math.floor(i / 2) < lines - 1 ? '1px solid #1b2538' : 'none',
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0, fontSize: isMobile ? '10.5px' : '12px', fontWeight: 600, color: '#94a3b8' }}>
              {f.label}
              {f.info}
            </span>
            <span style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: 700, color: '#f1f5f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, maxWidth: '100%' }}>{f.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Amber "An advanced strategy." callout for a slide's body, the same one
// Education uses. `children` is the rest of the sentence.
export function AdvancedNote({ children }) {
  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '12px', padding: '10px 12px', borderRadius: '10px', backgroundColor: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.25)' }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 20h16a2 2 0 0 0 1.73-2Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
      <span style={{ fontSize: '13px' }}>
        <strong style={{ color: '#fbbf24' }}>An advanced strategy.</strong> {children}
      </span>
    </div>
  )
}

// Small (i) with a hover / tap popover for side explanations on a slide.
// Re-enables pointer events so it also works inside a slide's (inert) visual.
// `align` picks which edge of the popover lines up with the (i): 'center',
// or 'start' / 'end' for an (i) near the left / right of the card, where a
// centred popover would be cut off. `placement="bottom"` opens it downward,
// for an (i) near the top of the stage.
export function InfoTip({ label, title, children, align = 'center', placement = 'top' }) {
  const [open, setOpen] = useState(false)
  const anchor = align === 'start'
    ? { left: '-8px' }
    : align === 'end'
      ? { right: '-8px' }
      : { left: '50%', transform: 'translateX(-50%)' }
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle', pointerEvents: 'auto' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={label}
        style={{ width: '16px', height: '16px', padding: 0, borderRadius: '50%', border: '1.5px solid #2d4a7a', backgroundColor: 'rgba(59,130,246,0.1)', color: '#60a5fa', fontSize: '10px', fontWeight: 800, fontFamily: 'inherit', lineHeight: 1, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      >
        i
      </button>
      {open && (
        <span style={{
          position: 'absolute', ...(placement === 'bottom' ? { top: 'calc(100% + 8px)' } : { bottom: 'calc(100% + 8px)' }), ...anchor, zIndex: 5,
          width: '270px', maxWidth: 'calc(100vw - 48px)', padding: '14px', borderRadius: '12px', boxSizing: 'border-box',
          backgroundColor: '#0f172a', border: '1px solid #26354f', boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
          fontSize: '12.5px', fontWeight: 400, color: '#94a3b8', lineHeight: 1.55, textAlign: 'left', whiteSpace: 'normal', textTransform: 'none', letterSpacing: 'normal',
        }}>
          <span style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#f1f5f9', marginBottom: '4px' }}>{title}</span>
          {children}
        </span>
      )}
    </span>
  )
}

const STYLES = `
  @keyframes ttFade { from { opacity: 0 } to { opacity: 1 } }
  @keyframes ttRise { from { opacity: 0; transform: translateY(10px) scale(0.96) } to { opacity: 1; transform: none } }
  @keyframes ttSlideIn { from { opacity: 0; transform: translateX(var(--tt-dir, 16px)) } to { opacity: 1; transform: none } }
  @keyframes ttRing { 0% { box-shadow: 0 0 0 0 rgba(96,165,250,0.55) } 70% { box-shadow: 0 0 0 10px rgba(96,165,250,0) } 100% { box-shadow: 0 0 0 0 rgba(96,165,250,0) } }
  .tt-overlay { position: fixed; inset: 0; z-index: 1100; background: rgba(0,0,0,0.62); backdrop-filter: blur(3px); animation: ttFade 0.25s ease both }
  /* A full-screen flex layer does the centring, so the card sits in the right
     place from its first frame; the card's own animation only scales and
     fades. Clicks pass through the layer to the overlay behind it. */
  .tt-center { position: fixed; inset: 0; z-index: 1101; display: flex; align-items: center; justify-content: center; padding: 12px; box-sizing: border-box; pointer-events: none }
  .tt-modal { position: relative; pointer-events: auto; width: 620px; max-width: 100%; max-height: calc(100dvh - 24px); transform-origin: 50% 55%; animation: ttRise 0.34s cubic-bezier(.16,1,.3,1) both }
  .tt-slide { animation: ttSlideIn 0.32s cubic-bezier(.2,.8,.2,1) }
  .tt-spot { border-radius: inherit; animation: ttRing 1.8s ease-out infinite; outline: 1.5px solid rgba(96,165,250,0.9); outline-offset: 3px }
  .tt-btn { transition: background-color 0.15s, border-color 0.15s, color 0.15s, transform 0.15s }
  .tt-btn:hover { transform: translateY(-1px) }
  .tt-link:hover { color: #f1f5f9 !important }
  /* Building blocks for slide visuals. Each plays once when its slide is shown
     (slides remount on every change), staggered with animation-delay. */
  @keyframes ttPop { from { opacity: 0; transform: translateY(6px) scale(0.6) } to { opacity: 1; transform: none } }
  @keyframes ttDraw { from { stroke-dashoffset: 1 } to { stroke-dashoffset: 0 } }
  @keyframes ttFade { from { opacity: 0 } to { opacity: 1 } }
  @keyframes ttRise { from { opacity: 0; transform: translateY(10px) scale(0.96) } to { opacity: 1; transform: none } }
  @keyframes ttGlow { 0%, 100% { box-shadow: 0 0 0 0 rgba(52,211,153,0.45), 0 10px 30px rgba(16,185,129,0.18) } 60% { box-shadow: 0 0 0 9px rgba(52,211,153,0), 0 10px 30px rgba(16,185,129,0.18) } }
  @keyframes ttGreenRing { 0%, 100% { box-shadow: 0 0 0 0 rgba(52,211,153,0.6) } 60% { box-shadow: 0 0 0 6px rgba(52,211,153,0) } }
  .tt-pop { opacity: 0; animation: ttPop 0.45s cubic-bezier(.2,.9,.3,1.3) forwards }
  .tt-draw { stroke-dasharray: 1; stroke-dashoffset: 1; animation: ttDraw 0.7s ease-out forwards }
  .tt-flow { stroke-dasharray: 0.07 0.93; stroke-dashoffset: 1; opacity: 0; animation: ttFade 0.3s ease forwards, ttDraw 1.8s linear infinite }
  .tt-fade { opacity: 0; animation: ttFade 0.4s ease forwards }
  .tt-rise { opacity: 0; animation: ttRise 0.5s cubic-bezier(.2,.8,.2,1) forwards }
  /* A page's real mobile card (bcm-card) clips to its rounded corners, which
     would clip TourMarkers too. Let them out and round the type strip by hand. */
  .tt-card { overflow: visible }
  .tt-card .bcm-card-type-strip { border-top-left-radius: 11px }
  .tt-card .bonus-outcome-row { overflow: visible }
  .tt-card .bonus-outcome-row > div:first-child { border-top-left-radius: 5px }
  @media (prefers-reduced-motion: reduce) {
    .tt-overlay, .tt-modal, .tt-slide, .tt-spot { animation: none }
    .tt-pop, .tt-draw, .tt-flow, .tt-fade, .tt-rise { animation: none; opacity: 1; stroke-dashoffset: 0 }
    .tt-flow { display: none }
  }
`

// Added to <head> once when the app loads, rather than rendered with the
// tour, so the keyframes already exist on the tour's first frame. Reusing the
// element by id keeps a dev hot reload from stacking copies.
if (typeof document !== 'undefined') {
  const el = document.getElementById('tt-styles') || Object.assign(document.createElement('style'), { id: 'tt-styles' })
  el.textContent = STYLES
  document.head.appendChild(el)
}

/**
 * slides: [{ title, body, visual }]
 * finalAction: { label, onClick } for the last slide (defaults to "Got it")
 * learnMore: { label, onClick } shown on the last slide
 */
export default function ToolTour({ open, onClose, slides, eyebrow, finalAction, learnMore, isMobile }) {
  const [index, setIndex] = useState(0)
  const [dir, setDir] = useState(1)
  const touchX = useRef(null)

  useEffect(() => { if (open) setIndex(0) }, [open])

  const last = index === slides.length - 1
  const go = useCallback((next) => {
    if (next < 0 || next >= slides.length) return
    setDir(next > index ? 1 : -1)
    setIndex(next)
  }, [index, slides.length])

  const finish = useCallback(() => {
    onClose()
    if (finalAction?.onClick) finalAction.onClick()
  }, [onClose, finalAction])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') last ? finish() : go(index + 1)
      else if (e.key === 'ArrowLeft') go(index - 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, index, last, go, finish, onClose])

  if (!open || !slides.length) return null
  const slide = slides[index]

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX }
  const onTouchEnd = (e) => {
    if (touchX.current == null) return
    const dx = e.changedTouches[0].clientX - touchX.current
    touchX.current = null
    if (Math.abs(dx) < 40) return
    if (dx < 0) last ? finish() : go(index + 1)
    else go(index - 1)
  }

  return createPortal(
    <>
      <div className="tt-overlay" onClick={onClose} />
      <div className="tt-center">
      <div
        className="tt-modal"
        role="dialog"
        aria-modal="true"
        aria-label={slide.title}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{
          overflowX: 'hidden', overflowY: 'auto', color: '#f1f5f9',
          borderRadius: isMobile ? '18px' : '20px',
          border: '1px solid #1b2538',
          background: 'linear-gradient(180deg, #0c1527 0%, #070d1a 100%)',
          boxShadow: '0 30px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)',
        }}
      >
        {/* Stage */}
        <div style={{
          position: 'relative', height: isMobile ? '270px' : '300px', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'radial-gradient(80% 90% at 50% 0%, rgba(59,130,246,0.16) 0%, rgba(59,130,246,0) 70%), radial-gradient(60% 70% at 100% 100%, rgba(139,92,246,0.10) 0%, rgba(139,92,246,0) 70%)',
          borderBottom: '1px solid #1b2538',
        }}>
          <div aria-hidden style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(148,163,184,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.05) 1px, transparent 1px)', backgroundSize: '28px 28px', maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)', WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)' }} />
          <button
            onClick={onClose}
            className="tt-link"
            style={{ position: 'absolute', top: '14px', right: '16px', zIndex: 2, background: 'none', border: 'none', color: '#64748b', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Skip
          </button>
          <div
            key={index}
            className="tt-slide"
            style={{ position: 'relative', width: '100%', padding: isMobile ? '24px 14px 14px' : '24px 40px', boxSizing: 'border-box', display: 'flex', justifyContent: 'center', pointerEvents: 'none', '--tt-dir': `${dir * 16}px` }}
          >
            {slide.visual}
          </div>
        </div>

        {/* Copy */}
        <div key={`copy-${index}`} className="tt-slide" style={{ padding: isMobile ? '18px 20px 6px' : '22px 28px 6px', '--tt-dir': `${dir * 10}px` }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {eyebrow ? `${eyebrow} · ` : ''}{index + 1} of {slides.length}
          </div>
          <div style={{ fontSize: isMobile ? '19px' : '21px', fontWeight: 800, letterSpacing: '-0.02em', marginTop: '6px' }}>{slide.title}</div>
          <div style={{ fontSize: '14px', color: '#94a3b8', lineHeight: 1.6, marginTop: '8px', minHeight: isMobile ? '66px' : '46px' }}>{slide.body}</div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: isMobile ? '14px 20px 20px' : '16px 28px 24px' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => go(i)}
                aria-label={`Slide ${i + 1}`}
                style={{ width: i === index ? '22px' : '7px', height: '7px', padding: 0, border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: i === index ? '#60a5fa' : 'rgba(148,163,184,0.25)', transition: 'width 0.25s, background-color 0.25s' }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {last && learnMore && (
              <button className="tt-link" onClick={() => { onClose(); learnMore.onClick() }} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: '0 4px' }}>
                {learnMore.label}
              </button>
            )}
            {index > 0 && (
              <button className="tt-btn" onClick={() => go(index - 1)} style={{ padding: '10px 16px', borderRadius: '10px', border: '1px solid #26354f', backgroundColor: 'transparent', color: '#cbd5e1', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Back
              </button>
            )}
            <button
              className="tt-btn"
              onClick={() => (last ? finish() : go(index + 1))}
              style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 6px 20px rgba(37,99,235,0.35)' }}
            >
              {last ? (finalAction?.label || 'Got it') : 'Next'}
            </button>
          </div>
        </div>
      </div>
      </div>
    </>,
    document.body
  )
}
